import type { LicenceTier } from "@/types";

/**
 * What a licence may say, checked before it is sent.
 *
 * Pure, and shared by the creation form and the licence tab, so the two
 * screens that write a licence cannot disagree about what one is. The backend
 * checks the same things again; this is what lets the button be disabled and
 * the field explain itself before anybody presses anything.
 */

export const TIERS: readonly LicenceTier[] = ["solo", "small", "medium", "large"];

/** Mirrors TIER_MAX_TEACHERS in functions/src/services/institutionService.ts. */
export const TIER_MAX_TEACHERS: Record<LicenceTier, number> = {
  solo: 1,
  small: 3,
  medium: 10,
  large: 40,
};

/** Mirrors STUDENT_SEATS_MAX in functions/src/types/institution.ts. */
export const STUDENT_SEATS_MAX = 10_000;

export interface LicenceDraft {
  tier: LicenceTier;
  teacherSeats: number;
  /** Undefined while the field is empty — a licence from before the limit existed. */
  studentSeats: number | undefined;
}

/** i18n keys, one per field that is wrong. An empty object is a licence that fits. */
export interface LicenceProblems {
  teacherSeats?: string;
  studentSeats?: string;
}

export function licenceProblems(draft: LicenceDraft): LicenceProblems {
  const problems: LicenceProblems = {};

  if (!Number.isInteger(draft.teacherSeats) || draft.teacherSeats < 1) {
    problems.teacherSeats = "institution.licence.seatsTooFew";
  } else if (draft.teacherSeats > TIER_MAX_TEACHERS[draft.tier]) {
    problems.teacherSeats = "institution.licence.seatsExceedTier";
  }

  if (draft.studentSeats === undefined) {
    // Required, not defaulted. A number nobody chose is not a licence term.
    problems.studentSeats = "institution.licence.studentsRequired";
  } else if (!Number.isInteger(draft.studentSeats) || draft.studentSeats < 1) {
    problems.studentSeats = "institution.licence.studentsTooFew";
  } else if (draft.studentSeats > STUDENT_SEATS_MAX) {
    problems.studentSeats = "institution.licence.studentsTooMany";
  }

  return problems;
}

export function licenceFits(draft: LicenceDraft): boolean {
  return Object.keys(licenceProblems(draft)).length === 0;
}

/**
 * A number field's text, as a number or as nothing.
 *
 * An empty field is "not set", never zero: zero would read as a licence that
 * allows no students, and the difference between "nobody decided" and "none"
 * is the whole reason the field can be empty.
 */
export function parseSeats(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
